<template>
  <Modal v-bind="$attrs" v-on="$listeners">
    <div v-if="loading" class="loading-mask">Loading...</div>

    <template #title>社員の名札</template>
    <template #subtitle>
      <span v-if="user">ローマ字・役職・顔写真はユーザーマスタに保存されます</span>
      <span v-else>ユーザーマスタに登録されている社員から選んでください</span>
    </template>

    <!-- 社員を選ぶ間は候補の一覧が下に開くので、その分の高さを先に取っておく（モーダルの中でスクロールバーが出ないように） -->
    <div class="np-body" :class="!fixedUser && !user ? 'np-picking' : ''">
      <div v-if="loadError" class="np-error">{{ loadError }}</div>

      <!-- どの社員か（雇い入れ（社員）のタスクから開いたとき。ユーザーマスタに無い人は作れない） -->
      <div v-if="!fixedUser" class="np-row">
        <label class="np-label">社員</label>
        <multiselect
          :value="user"
          :options="users"
          :multiple="false"
          :show-labels="false"
          :searchable="true"
          :allow-empty="false"
          :max-height="260"
          :custom-label="userLabel"
          track-by="id"
          placeholder="名前で探して選んでください"
          class="np-select"
          @input="pickUser"
        >
          <span slot="noResult">ユーザーマスタにいません。先にユーザーマスタに登録してください</span>
          <span slot="noOptions">ユーザーがいません</span>
        </multiselect>
        <div v-if="!loading && !loadError" class="np-hint">候補 {{ users.length }} 名（退職日を過ぎた人は出ません）</div>
      </div>

      <template v-if="user">
        <!-- 氏名・部署（表示だけ） -->
        <div class="np-row">
          <label class="np-label">氏名</label>
          <div class="np-static">{{ user.userName }}</div>
        </div>
        <div class="np-row">
          <label class="np-label">部署</label>
          <div class="np-static">{{ user.departmentName || '（主な部署が無い）' }}</div>
        </div>

        <!-- 役職・ローマ字（保存する） -->
        <div class="np-row">
          <label class="np-label">役職</label>
          <input v-model="form.userPost" type="text" class="np-input w-48" placeholder="例：部長" maxlength="50" />
        </div>
        <div class="np-row">
          <label class="np-label">ローマ字</label>
          <input v-model="form.userRomaji" type="text" class="np-input w-72" maxlength="100" />
        </div>

        <!-- 顔写真（その場で保存） -->
        <div class="np-row">
          <label class="np-label">顔写真</label>
          <div class="flex items-center gap-3">
            <span :class="user.hasPhoto ? 'np-yes' : 'np-no'">{{ user.hasPhoto ? 'あり' : 'なし' }}</span>
            <label class="np-link" :class="photoSaving ? 'np-disabled' : ''">
              <input type="file" accept=".jpg,.jpeg,.png" class="hidden" :disabled="photoSaving" @change="pickPhoto" />{{ user.hasPhoto ? '差し替え' : '登録' }}
            </label>
            <button v-if="user.hasPhoto" type="button" class="np-link danger" :disabled="photoSaving" @click="deletePhoto">削除</button>
            <span v-if="photoSaving" class="np-sub animate-pulse">保存中…</span>
          </div>
          <div class="np-hint">JPEG か PNG。小さくして保存します。アポロ標準・野村・三菱の札に入ります</div>
        </div>

        <!-- 出す名札の種類 -->
        <div class="np-row">
          <label class="np-label">出す名札</label>
          <div class="flex flex-wrap gap-x-5 gap-y-1">
            <label v-for="t in types" :key="t.key" class="np-check">
              <input v-model="t.on" type="checkbox" />{{ t.name }}
            </label>
          </div>
          <div class="np-hint">アポロ標準は裏も表と同じ。ほかは裏が緊急連絡先。A4 1 枚に左が表・右が裏で並びます</div>
        </div>
      </template>
    </div>

    <template #action>
      <div class="flex items-center gap-3 w-full">
        <span v-if="dirty" class="text-xs text-yellow-700 font-bold">役職・ローマ字は「保存・名札作成」で保存されます</span>
        <span class="ml-auto flex items-center gap-3">
          <PrimaryButton text="閉じる" size="normal bg-gray-400 hover:bg-gray-500" @click="$emit('input', false)" />
          <!-- 役職・ローマ字を保存してから名札の PDF を作る（保存だけのボタンは無い。ユーザー指示） -->
          <PrimaryButton text="保存・名札作成" :disabled="!user || saving || making || !types.some(t => t.on)" @click="make()" />
        </span>
      </div>
    </template>
  </Modal>
</template>

<script>
import Modal from '@components/Modal.vue'
import PrimaryButton from '@components/PrimaryButton.vue'
import Multiselect from 'vue-multiselect'
import * as backend from '@libs/backend'
import * as dialogs from '@libs/dialogs'

// 出す名札の種類（アポロ標準だけ初期 ON。ユーザー決定）
const TYPES = [
  { key: 'apollo', name: 'アポロ標準' },
  { key: 'mitsui', name: '三井' },
  { key: 'mf', name: 'エム・エフ' },
  { key: 'nomura', name: '野村' },
  { key: 'mitsubishi', name: '三菱' }
]

/**
 * File → base64 の data URL（バックへ送って S3 に上げる）
 */
function readFileAsDataUrl (file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

export default {
  components: {
    Modal,
    PrimaryButton,
    Multiselect
  },

  props: {
    // 開くたびに増える（開いたときに読み直す）
    open: {
      type: Number,
      default: 0
    },
    // ユーザーマスタの行から開いたときのユーザー ID（無ければ一覧から選ぶ）
    userId: {
      type: [Number, String],
      default: null
    }
  },

  data() {
    return {
      loading: false,
      // 読み込みに失敗したときの文（画面に出す）
      loadError: '',
      saving: false,
      making: false,
      photoSaving: false,
      // 選べる社員（タスクから開いたとき）
      users: [],
      // 今の社員
      user: null,
      // 役職・ローマ字の入力
      form: { userPost: '', userRomaji: '' },
      types: TYPES.map(t => ({ ...t, on: t.key === 'apollo' }))
    }
  },

  computed: {
    /**
     * ユーザー ID を渡されて開いたか（社員を選ばせない）
     */
    fixedUser() {
      return !!this.userId
    },

    /**
     * 役職・ローマ字が保存した値と違うか
     */
    dirty() {
      return !!this.user && ((this.user.userPost || '') !== this.form.userPost || (this.user.userRomaji || '') !== this.form.userRomaji)
    }
  },

  watch: {
    open() {
      this.load()
    }
  },

  methods: {
    /**
     * 開いたときに読み直す（ID があればその人、無ければ一覧）
     * API: user/nameplateUser・user/nameplateUsers
     */
    async load() {
      this.user = null
      this.users = []
      this.form = { userPost: '', userRomaji: '' }
      this.types = TYPES.map(t => ({ ...t, on: t.key === 'apollo' }))
      this.loadError = ''
      this.loading = true
      try {
        if (this.fixedUser) {
          const res = await backend.searchData('user/nameplateUser', { id: this.userId })
          this.setUser(res.data ? res.data.data : null)
          if (!this.user) {
            this.loadError = 'このユーザーが見つかりません。ユーザーマスタを読み直してください。'
          }
        } else {
          const res = await backend.searchData('user/nameplateUsers', {})
          this.users = res.data && Array.isArray(res.data.data) ? res.data.data : []
        }
      } catch (e) {
        console.log(e)
        // 列がまだ無い・API が無いなどで取れなかったときは、黙って空にせず画面に出す
        this.loadError = '社員の一覧を取得できませんでした。RP_api のログを確認してください（mst_user に user_romaji・user_post の列があるか、user/nameplate… の API が動いているか）。'
      } finally {
        this.loading = false
      }
    },

    /**
     * 今の社員を入れ替える（入力欄も保存済みの値にそろえる）
     */
    setUser(user) {
      this.user = user
      this.form = { userPost: user ? (user.userPost || '') : '', userRomaji: user ? (user.userRomaji || '') : '' }
    },

    pickUser(user) {
      this.setUser(user)
    },

    userLabel(u) {
      return `${u.userName}${u.departmentName ? `（${u.departmentName}）` : ''}`
    },

    /**
     * 役職・ローマ字を保存する
     * API: user/nameplateSave
     * @returns 保存できたか
     */
    async save() {
      if (!this.user) {
        return false
      }
      this.saving = true
      try {
        const res = await backend.postData('user/nameplateSave', { id: this.user.id, userPost: this.form.userPost, userRomaji: this.form.userRomaji, updateUser: this.$store.getters.user.id })
        if (res.data && res.data.data) {
          this.setUser(res.data.data)
          this.$emit('saved', this.user)
          return true
        }
        await dialogs.showErrorDialog('保存できません', '保存に失敗しました。もう一度お試しください。')
        return false
      } catch (e) {
        console.log(e)
        return false
      } finally {
        this.saving = false
      }
    },

    /**
     * 名札の PDF を作って別のタブで開く（先に役職・ローマ字を保存する）
     * タブは押した直後に開いておく（作り終わってから開くとポップアップとして止められる）
     * API: user/nameplateRender
     */
    async make() {
      if (!this.user) {
        return
      }
      const types = this.types.filter(t => t.on).map(t => t.key)
      if (!types.length) {
        return
      }
      this.making = true
      const tab = window.open('', '_blank')
      if (tab) {
        tab.document.title = `名札 ${this.user.userName}`
        tab.document.body.innerHTML = '<p style="font-family:sans-serif;padding:16px">名札を作っています…</p>'
      }
      try {
        if (this.dirty && !(await this.save())) {
          if (tab) {
            tab.close()
          }
          return
        }
        const res = await backend.postBlob2('user/nameplateRender', { id: this.user.id, types })
        const url = (window.URL || window.webkitURL).createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
        if (tab) {
          tab.location.href = url
        } else {
          window.open(url, '_blank')
        }
      } catch (e) {
        console.log(e)
        if (tab) {
          tab.close()
        }
        await dialogs.showErrorDialog('名札', '名札を作れませんでした。もう一度お試しください。')
      } finally {
        this.making = false
      }
    },

    /**
     * 顔写真を選んだ：JPEG・PNG だけ受け付け、その場で保存（バックで長辺 480px に縮めて S3 へ）
     * API: user/nameplateSavePhoto
     */
    async pickPhoto(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file || !this.user) {
        return
      }
      const ext = (String(file.name).match(/\.([A-Za-z0-9]+)$/) || [])[1]
      if (!['jpg', 'jpeg', 'png'].includes(String(ext || '').toLowerCase())) {
        await dialogs.showErrorDialog('顔写真', `「${file.name}」は置けない種類です。JPEG か PNG の写真を選んでください。`)
        return
      }
      this.photoSaving = true
      try {
        const data = await readFileAsDataUrl(file)
        await backend.postData('user/nameplateSavePhoto', { id: this.user.id, name: file.name, data, updateUser: this.$store.getters.user.id })
        this.user.hasPhoto = true
        this.$emit('saved', this.user)
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('顔写真', '顔写真を保存できませんでした。画像のファイルか確かめて、もう一度お試しください。')
      } finally {
        this.photoSaving = false
      }
    },

    /**
     * 顔写真を消す（確認あり）
     * API: user/nameplateDeletePhoto
     */
    async deletePhoto() {
      if (!this.user) {
        return
      }
      const answer = await dialogs.showConfirmDialog('顔写真の削除', `${this.user.userName} の顔写真を消します。よろしいですか。`)
      if (answer !== 'YES') {
        return
      }
      this.photoSaving = true
      try {
        await backend.postData('user/nameplateDeletePhoto', { id: this.user.id, updateUser: this.$store.getters.user.id })
        this.user.hasPhoto = false
        this.$emit('saved', this.user)
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('顔写真', '顔写真を消せませんでした。もう一度お試しください。')
      } finally {
        this.photoSaving = false
      }
    }
  }
}
</script>

<style scoped>
.loading-mask {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.7);
  color: #6b7280;
  font-weight: bold;
}

.np-body {
  width: 780px;
  max-width: 86vw;
  padding: 4px 4px 8px;
}

/* 社員を選ぶ間は候補の一覧（最大 260px）が入る高さを確保しておく */
.np-body.np-picking {
  min-height: 380px;
}

.np-error {
  margin: 4px 0 8px;
  padding: 8px 10px;
  border: 1px solid #fca5a5;
  border-radius: 4px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 0.8rem;
  font-weight: bold;
}

.np-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
  padding: 7px 0;
  border-bottom: 1px solid #f3f4f6;
}

.np-row:last-child {
  border-bottom: none;
}

.np-label {
  width: 72px;
  flex-shrink: 0;
  font-size: 0.8rem;
  font-weight: bold;
  color: #4b5563;
}

.np-static {
  font-size: 0.95rem;
  font-weight: bold;
  color: #1f2937;
}

.np-sub {
  font-size: 0.75rem;
  font-weight: normal;
  color: #6b7280;
}

.np-input {
  height: 36px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 0 8px;
  font-size: 0.9rem;
}

.np-select {
  flex: 1;
  min-width: 320px;
  max-width: calc(100% - 90px);
}

/* 補足は項目名の幅の分だけ右から始める */
.np-hint {
  flex-basis: 100%;
  padding-left: 84px;
  font-size: 0.72rem;
  color: #6b7280;
}

.np-yes {
  font-weight: bold;
  color: #047857;
}

.np-no {
  font-weight: bold;
  color: #6b7280;
}

.np-link {
  font-size: 0.8rem;
  color: #2563eb;
  text-decoration: underline;
  cursor: pointer;
}

.np-link.danger {
  color: #dc2626;
}

.np-link:disabled,
.np-link.np-disabled {
  color: #9ca3af;
  cursor: not-allowed;
}

.np-check {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.85rem;
  cursor: pointer;
}

.np-check input[type='checkbox'] {
  color: #4f46e5;
}
</style>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
